// src/components/NFTMinting.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  Upload, 
  Image as ImageIcon, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Loader,
  ExternalLink,
  Copy,
  Coffee,
  RefreshCw
} from 'lucide-react';
import './NFTMinting.css';

const NFTMinting = ({ account, onBack, user, userProfile }) => {
  const [nftData, setNftData] = useState({
    name: '',
    description: '',
    image: null,
    attributes: []
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isMinting, setIsMinting] = useState(false);
  const [mintStatus, setMintStatus] = useState('idle'); // 'idle', 'uploading', 'minting', 'success', 'error'
  const [mintResult, setMintResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [cafePoints, setCafePoints] = useState(0);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);
  const [mintingRights, setMintingRights] = useState({ hasRights: false, numPoints: 0 });
  const [isCheckingRights, setIsCheckingRights] = useState(false);
  const fileInputRef = useRef(null);

  const MINTING_COST = 10; // NFT 발행 1회당 필요한 CAFE Points

  // 이미지 업로드 처리
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      setErrors(['이미지 파일만 업로드 가능합니다.']);
      return;
    }

    // 파일 크기 검증 (10MB 제한)
    if (file.size > 10 * 1024 * 1024) {
      setErrors(['이미지 크기는 10MB 이하여야 합니다.']);
      return;
    }

    setNftData(prev => ({ ...prev, image: file }));
    
    // 미리보기 생성
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setErrors([]);
  };

  // CAFE Points 조회
  const fetchCafePoints = async () => {
    if (!user?.email) {
      setCafePoints(0);
      return;
    }

    setIsLoadingPoints(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(
        `${serverUrl}/api/cafe/points/balance?email=${encodeURIComponent(user.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCafePoints(data.cafePoints || 0);
      
      // 발행 권한 확인
      setMintingRights({
        hasRights: (data.cafePoints || 0) >= MINTING_COST,
        numPoints: data.cafePoints || 0
      });
      
      console.log('✅ CAFE Points 조회 성공:', data);

    } catch (error) {
      console.error('❌ CAFE Points 조회 실패:', error);
      setCafePoints(0);
      setMintingRights({ hasRights: false, numPoints: 0 });
    } finally {
      setIsLoadingPoints(false);
    }
  };

  // 컴포넌트 마운트 시 포인트 조회
  useEffect(() => {
    if (user?.email) {
      fetchCafePoints();
    }
  }, [user?.email]);

  // 속성 추가
  const addAttribute = () => {
    setNftData(prev => ({
      ...prev,
      attributes: [...prev.attributes, { trait_type: '', value: '' }]
    }));
  };

  // 속성 제거
  const removeAttribute = (index) => {
    setNftData(prev => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index)
    }));
  };

  // 속성 업데이트
  const updateAttribute = (index, field, value) => {
    setNftData(prev => ({
      ...prev,
      attributes: prev.attributes.map((attr, i) => 
        i === index ? { ...attr, [field]: value } : attr
      )
    }));
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors = [];

    if (!nftData.name.trim()) {
      newErrors.push('NFT 이름을 입력해주세요.');
    }

    if (!nftData.description.trim()) {
      newErrors.push('NFT 설명을 입력해주세요.');
    }

    if (!nftData.image) {
      newErrors.push('NFT 이미지를 업로드해주세요.');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };
 
  // IPFS에 이미지 업로드 (실제 구현)
  const uploadToIPFS = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userInfo', JSON.stringify({
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname || user.displayName
      }));

      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(`${serverUrl}/api/nft/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'IPFS 업로드 실패');
      }

      const result = await response.json();
      console.log('✅ 이미지 IPFS 업로드 성공:', result);
      
      return result.ipfsUrl; // ipfs://QmHash...

    } catch (error) {
      console.error('❌ 이미지 IPFS 업로드 실패:', error);
      throw error;
    }
  };

  // 메타데이터 생성 및 업로드 (실제 구현)
  const uploadMetadata = async (imageUri) => {
    try {
      const metadata = {
        name: nftData.name,
        description: nftData.description,
        image: imageUri,
        attributes: nftData.attributes.filter(attr => attr.trait_type && attr.value),
        external_url: 'https://worldlandcafe.com',
        created_by: user.email,
        created_at: new Date().toISOString()
      };

      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(`${serverUrl}/api/nft/upload-metadata`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userInfo: {
            uid: user.uid,
            email: user.email,
            nickname: userProfile?.nickname || user.displayName
          },
          metadata: metadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '메타데이터 업로드 실패');
      }

      const result = await response.json();
      console.log('✅ 메타데이터 IPFS 업로드 성공:', result);
      
      return result.ipfsUrl; // ipfs://QmHash...

    } catch (error) {
      console.error('❌ 메타데이터 IPFS 업로드 실패:', error);
      throw error;
    }
  };

  // NFT 민팅 (ERC-721) 
  const mintNFT = async (metadataUri) => {
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    // ✅ 실제 배포된 컨트랙트 주소로 변경
    const nftContractAddress = '0xc3d64CBB90CA3A4ec30D4E2Acab9a01899552af8';
    
    // ERC-721 mint function signature
    // function mint(address to, string memory uri)
    const mintFunction = '0xd0def521'; // mint(address,string) 함수 시그니처
    
    // 주소 인코딩 (0x 제거 후 64자리로 패딩)
    const paddedAddress = account.substring(2).padStart(64, '0');
    
    // 문자열 인코딩 (ABI 인코딩 규칙) - 브라우저 호환
    const encoder = new TextEncoder();
    const metadataUriBytes = Array.from(encoder.encode(metadataUri))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    const metadataUriLength = metadataUri.length.toString(16).padStart(64, '0');
    
    // 문자열 데이터는 32바이트 단위로 패딩
    const paddedMetadataUri = metadataUriBytes.padEnd(
      Math.ceil(metadataUriBytes.length / 64) * 64, 
      '0'
    );
    
    // 데이터 구조:
    // 1. 함수 시그니처 (4바이트)
    // 2. to 주소 (32바이트)
    // 3. 문자열 데이터 오프셋 (32바이트) - 항상 0x40 (64바이트)
    // 4. 문자열 길이 (32바이트)
    // 5. 문자열 데이터 (32바이트 단위로 패딩)
    const data = 
      mintFunction + 
      paddedAddress + 
      '0000000000000000000000000000000000000000000000000000000000000040' + 
      metadataUriLength + 
      paddedMetadataUri;

    const txParams = {
      from: account,
      to: nftContractAddress,
      data: data,
      gas: '0xC3500', // 800000 gas (충분한 가스 제공)
    };

    try {
      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });

      console.log('✅ NFT 발행 트랜잭션 전송 성공:', txHash);
      return txHash;

    } catch (error) {
      console.error('❌ NFT 발행 트랜잭션 실패:', error);
      throw error;
    }
  };

  // NFT 발행 실행
  const executeMinting = async () => {
    if (!validateForm()) return;

    // 권한 재확인
    if (!mintingRights.hasRights || mintingRights.numPoints < MINTING_COST) {
      alert(`NFT 발행 권한이 부족합니다. ${MINTING_COST} CAFE Points가 필요합니다.\n현재 보유: ${mintingRights.numPoints} Points`);
      return;
    }

    // 권한 사용 확인
    const confirmMessage = `NFT를 발행하면 ${MINTING_COST} CAFE Points가 차감됩니다.\n\n현재 보유 포인트: ${mintingRights.numPoints} Points\n발행 후 잔여 포인트: ${mintingRights.numPoints - MINTING_COST} Points\n\n계속하시겠습니까?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsMinting(true);
    setMintStatus('uploading');
    setMintResult(null);

    try {
      // 서버에 포인트 차감 요청
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const pointsResponse = await fetch(`${serverUrl}/api/cafe/points/use-minting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          uid: user.uid,
          email: user.email, 
          pointsCost: MINTING_COST
        })
      });

      if (!pointsResponse.ok) {
        const errorData = await pointsResponse.json();
        throw new Error(errorData.error || '포인트 차감 실패');
      }

      const pointsResult = await pointsResponse.json();
      if (!pointsResult.success) {
        throw new Error(pointsResult.error || '포인트 사용 실패');
      }

      console.log('✅ NFT 발행 포인트 차감 승인:', pointsResult);

      // 포인트 상태 업데이트
      setMintingRights(prev => ({
        ...prev,
        numPoints: pointsResult.remainingPoints,
        hasRights: pointsResult.remainingPoints >= MINTING_COST
      }));
      setCafePoints(pointsResult.remainingPoints);

      // 1단계: 이미지 IPFS 업로드
      console.log('📤 이미지 업로드 중...');
      const imageUri = await uploadToIPFS(nftData.image);
      
      // 2단계: 메타데이터 생성 및 업로드
      console.log('📝 메타데이터 생성 중...');
      setMintStatus('minting');
      const metadataUri = await uploadMetadata(imageUri);
      
      // 3단계: NFT 민팅
      console.log('🎨 NFT 발행 중...');
      const txHash = await mintNFT(metadataUri);
      
      setMintStatus('success');
      setMintResult({
        success: true,
        txHash: txHash,
        imageUri: imageUri,
        metadataUri: metadataUri,
        name: nftData.name,
        pointsUsed: MINTING_COST,
        remainingPoints: pointsResult.remainingPoints
      });

      console.log('✅ NFT 발행 성공:', txHash);

    } catch (error) {
      console.error('❌ NFT 발행 실패:', error);
      
      let errorMessage = 'NFT 발행 중 오류가 발생했습니다.';
      
      if (error.code === 4001) {
        errorMessage = '사용자가 거래를 취소했습니다.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMintStatus('error');
      setMintResult({
        success: false,
        error: errorMessage
      });
      setErrors([errorMessage]);

    } finally {
      setIsMinting(false);
    }
  };

  // 결과 초기화
  const resetForm = () => {
    setNftData({
      name: '',
      description: '',
      image: null,
      attributes: []
    });
    setImagePreview(null);
    setMintStatus('idle');
    setMintResult(null);
    setErrors([]);
  };

  // 클립보드 복사
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('복사되었습니다!');
    });
  };

  return (
    <div className="nft-minting">
      {/* Header */}
      <div className="minting-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft className="icon" />
          뒤로가기
        </button>
        <h1>NFT 발행</h1>
      </div>

      {/* CAFE Points 표시 섹션 */}
      <div className="points-info-section">
        <div className="points-card">
          <div className="points-header">
            <Coffee className="icon" />
            <div className="points-details">
              <span className="points-label">보유 CAFE Points</span>
              <div className="points-value">
                {isLoadingPoints ? (
                  <div className="loading-points">
                    <div className="spinner-small"></div>
                    <span>조회 중...</span>
                  </div>
                ) : (
                  <>
                    <span className="points-number">{cafePoints}</span>
                    <span className="points-unit">Points</span>
                  </>
                )}
              </div>
            </div>
            <button 
              onClick={fetchCafePoints}
              className="refresh-points-btn"
              disabled={isLoadingPoints}
              title="포인트 새로고침"
            >
              <RefreshCw className={`icon ${isLoadingPoints ? 'spinning' : ''}`} />
            </button>
          </div>
          
          <div className="minting-cost-info">
            <div className="cost-detail">
              <span className="cost-label">NFT 발행 비용:</span>
              <span className="cost-value">{MINTING_COST} Points / 1회</span>
            </div>
            <div className={`availability ${mintingRights.hasRights ? 'available' : 'unavailable'}`}>
              {mintingRights.hasRights ? (
                <>
                  <CheckCircle className="icon" />
                  <span>발행 가능 ({Math.floor(cafePoints / MINTING_COST)}회)</span>
                </>
              ) : (
                <>
                  <AlertCircle className="icon" />
                  <span>포인트 부족 ({MINTING_COST - cafePoints} Points 필요)</span>
                </>
              )}
            </div>
          </div>

          {!mintingRights.hasRights && (
            <div className="insufficient-points-notice">
              <p>NFT 발행을 위해서는 최소 {MINTING_COST} CAFE Points가 필요합니다.</p>
              <p className="contact-info">
                포인트가 필요한 경우 관리자에게 문의하세요: 
                <a href="mailto:worldlandcafe@gmail.com" className="contact-link">
                  worldlandcafe@gmail.com
                </a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="minting-content">
        {/* Image Upload Section */}
        <div className="upload-section">
          <h3>NFT 이미지</h3>
          <div className="image-upload-area">
            {imagePreview ? (
              <div className="image-preview">
                <img src={imagePreview} alt="NFT Preview" />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="change-image-btn"
                >
                  <Upload className="icon" />
                  이미지 변경
                </button>
              </div>
            ) : (
              <div 
                className="upload-placeholder"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="upload-icon" />
                <p>클릭하여 이미지 업로드</p>
                <span className="upload-hint">JPG, PNG, GIF (최대 10MB)</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* NFT Information */}
        <div className="nft-info-section">
          <h3>NFT 정보</h3>
          
          <div className="form-group">
            <label>이름 *</label>
            <input
              type="text"
              placeholder="NFT 이름을 입력하세요"
              value={nftData.name}
              onChange={(e) => setNftData(prev => ({ ...prev, name: e.target.value }))}
              className="input-field"
              disabled={isMinting}
            />
          </div>

          <div className="form-group">
            <label>설명 *</label>
            <textarea
              placeholder="NFT에 대한 설명을 입력하세요"
              value={nftData.description}
              onChange={(e) => setNftData(prev => ({ ...prev, description: e.target.value }))}
              className="textarea-field"
              rows="4"
              disabled={isMinting}
            />
          </div>
        </div>

        {/* Attributes Section */}
        <div className="attributes-section">
          <div className="attributes-header">
            <h3>속성 (선택사항)</h3>
            <button 
              onClick={addAttribute}
              className="add-attribute-btn"
              disabled={isMinting}
            >
              + 속성 추가
            </button>
          </div>

          {nftData.attributes.map((attr, index) => (
            <div key={index} className="attribute-item">
              <input
                type="text"
                placeholder="속성명 (예: 배경)"
                value={attr.trait_type}
                onChange={(e) => updateAttribute(index, 'trait_type', e.target.value)}
                className="input-field"
                disabled={isMinting}
              />
              <input
                type="text"
                placeholder="값 (예: 파란색)"
                value={attr.value}
                onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                className="input-field"
                disabled={isMinting}
              />
              <button 
                onClick={() => removeAttribute(index)}
                className="remove-attribute-btn"
                disabled={isMinting}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="errors-section">
            <div className="error-header">
              <AlertCircle className="icon" />
              <span>오류</span>
            </div>
            <ul className="error-list">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Minting Progress */}
        {isMinting && (
          <div className="minting-progress">
            <div className="progress-header">
              <Loader className="icon spinning" />
              <span>NFT 발행 중...</span>
            </div>
            <div className="progress-steps">
              <div className={`step ${mintStatus === 'uploading' || mintStatus === 'minting' || mintStatus === 'success' ? 'active' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">이미지 업로드</span>
              </div>
              <div className={`step ${mintStatus === 'minting' || mintStatus === 'success' ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">메타데이터 생성</span>
              </div>
              <div className={`step ${mintStatus === 'success' ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">NFT 발행</span>
              </div>
            </div>
          </div>
        )}

        {/* Mint Result */}
        {mintResult && !isMinting && (
          <div className={`result-section ${mintResult.success ? 'success' : 'error'}`}>
            <div className="result-header">
              {mintResult.success ? (
                <>
                  <CheckCircle className="icon" />
                  <span>NFT 발행 완료!</span>
                </>
              ) : (
                <>
                  <AlertCircle className="icon" />
                  <span>발행 실패</span>
                </>
              )}
            </div>

            {mintResult.success ? (
              <div className="result-content">
                <div className="result-info">
                  <p className="result-message">
                    "{mintResult.name}" NFT가 성공적으로 발행되었습니다!
                  </p>
                  
                  <div className="points-used-info">
                    <p className="points-used">
                      💰 사용된 CAFE Points: <strong>{mintResult.pointsUsed}</strong>
                    </p>
                    <p className="points-remaining">
                      💎 남은 CAFE Points: <strong>{mintResult.remainingPoints}</strong>
                    </p>
                  </div>
                  
                  <div className="result-details">
                    <div className="detail-item">
                      <span className="label">거래 해시:</span>
                      <div className="value-group">
                        <span className="value">
                          {mintResult.txHash.substring(0, 10)}...{mintResult.txHash.substring(mintResult.txHash.length - 8)}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(mintResult.txHash)}
                          className="copy-btn"
                        >
                          <Copy className="icon" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="detail-item">
                      <span className="label">이미지 URI:</span>
                      <div className="value-group">
                        <span className="value">{mintResult.imageUri}</span>
                        <button 
                          onClick={() => copyToClipboard(mintResult.imageUri)}
                          className="copy-btn"
                        >
                          <Copy className="icon" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="result-actions">
                    <a 
                      href={`https://scan.worldland.foundation/tx/${mintResult.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="explorer-btn"
                    >
                      <ExternalLink className="icon" />
                      Explorer에서 보기
                    </a>
                    <button 
                      onClick={resetForm}
                      className="new-nft-btn"
                    >
                      새 NFT 발행
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="result-content">
                <p className="error-message">{mintResult.error}</p>
                <button 
                  onClick={() => setMintResult(null)}
                  className="retry-btn"
                >
                  다시 시도
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mint Button */}
        {!mintResult && (
          <div className="mint-section">
            <div className="mint-info">
              <FileText className="icon" />
              <p>NFT 발행 시 {MINTING_COST} CAFE Points와 가스비(WLC)가 필요합니다. 충분한 포인트와 WLC 잔액을 확인해주세요.</p>
            </div>
            <button 
              onClick={executeMinting}
              disabled={isMinting || !mintingRights.hasRights}
              className="mint-btn"
            >
              {isMinting ? (
                <>
                  <Loader className="icon spinning" />
                  발행 중...
                </>
              ) : (
                <>
                  <ImageIcon className="icon" />
                  NFT 발행하기 ({MINTING_COST} Points)
                </>
              )}
            </button>
            {!mintingRights.hasRights && (
              <p className="insufficient-warning">
                ⚠️ CAFE Points가 부족하여 발행할 수 없습니다.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NFTMinting;